import React, { useState, useEffect } from "react";
import { Select, Input, Button, message, Spin, Tag } from "antd";
import { controller as loginController } from "../controller";
import { controller as api } from "../../controller";
import {
  formatCodesMultiline,
  parseProcedureCodeTextarea,
  rowsToMap,
  CategoryMapEditor,
  DEFAULT_COVERAGE_CATEGORIES,
} from "../../components/OfficeInsuranceCarriers/procedureCodeEditors";

// Onboarding "procedure code defaults" step. Reuses the SAME per-office override
// endpoints + editor as the My Office "Insurance Carriers" page: per carrier, the
// clinic reviews the effective breakdown procedure codes + coverage-category map and
// overrides them for their office. Optional — Continue / Set up later both advance
// via complete-procedure-code-defaults-step.
const ProcedureCodeDefaults = (props) => {
  const officeId = localStorage.getItem("selectedOffice");
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState(null);
  const [loadingCarrier, setLoadingCarrier] = useState(false);
  const [saving, setSaving] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  const [codesText, setCodesText] = useState("");
  const [codesSource, setCodesSource] = useState(null);
  const [hasCodeOverride, setHasCodeOverride] = useState(false);
  const [catRows, setCatRows] = useState([]);
  const [catDefaults, setCatDefaults] = useState({});
  const [hasCatOverride, setHasCatOverride] = useState(false);
  const [validCategories, setValidCategories] = useState(DEFAULT_COVERAGE_CATEGORIES);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.listInsuranceProviders();
        const list = (res && res.data) || [];
        setProviders(list.filter((p) => p && p.is_active !== false));
      } catch (e) {
        /* leave empty — the picker just won't suggest carriers */
      }
    })();
  }, []);

  // Load the effective codes + category map for a carrier (override, else default).
  const loadCarrier = async (pid) => {
    setProviderId(pid || null);
    if (!pid || !officeId) return;
    setLoadingCarrier(true);
    try {
      const codes = await api.getOfficeBreakdownProcedureCodes(officeId, pid);
      const effCodes = (codes && (codes.override_codes || codes.effective_codes)) || [];
      setCodesText(formatCodesMultiline(effCodes));
      setCodesSource(codes && codes.source);
      setHasCodeOverride(!!(codes && codes.override_codes));

      const cats = await api.getOfficeProcedureCategories(officeId, pid);
      // Show the SYSTEM default category for every code we fetch, with the office's
      // own overrides applied on top. Keep the defaults so save can persist only
      // the codes actually changed (a sparse override).
      const defMap = (cats && cats.default_map) || {};
      const ovMap = (cats && cats.override_map) || {};
      const codeKeys = Array.from(new Set([...Object.keys(defMap), ...Object.keys(ovMap)]));
      setCatDefaults(defMap);
      setCatRows(codeKeys.map((code) => ({ code, category: ovMap[code] || defMap[code] })));
      setHasCatOverride(!!(ovMap && Object.keys(ovMap).length));
      setValidCategories((cats && cats.valid_categories) || DEFAULT_COVERAGE_CATEGORIES);
    } catch (e) {
      message.error("Couldn't load this carrier's defaults.");
    } finally {
      setLoadingCarrier(false);
    }
  };

  const saveCarrier = async () => {
    if (!providerId || !officeId) return;
    setSaving(true);
    try {
      const r1 = await api.setOfficeBreakdownProcedureCodes(
        officeId,
        providerId,
        parseProcedureCodeTextarea(codesText)
      );
      // Persist only categories that DIFFER from the system default, so codes left
      // at their default aren't frozen as overrides (and future default changes
      // still apply). An empty result clears the office override entirely.
      const fullMap = rowsToMap(catRows);
      const overrideMap = {};
      Object.keys(fullMap).forEach((code) => {
        if (fullMap[code] && fullMap[code] !== catDefaults[code]) overrideMap[code] = fullMap[code];
      });
      const r2 = await api.setOfficeProcedureCategories(officeId, providerId, overrideMap);
      const ok = r1 && r1._httpStatus < 250 && r2 && r2._httpStatus < 250;
      if (ok) {
        message.success("Saved for this carrier");
        await loadCarrier(providerId); // refresh the override badges
      } else {
        message.error("Couldn't save. Please check the values and try again.");
      }
    } catch (e) {
      message.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const advance = async () => {
    if (advancing) return;
    setAdvancing(true);
    const res = await loginController.completeProcedureCodeDefaultsStep();
    setAdvancing(false);
    if (res && res.status < 250) props.readOnboardingStatus();
    else message.error("Could not continue. Please try again.");
  };

  const codeBadge = hasCodeOverride
    ? "Overridden for your office"
    : `Using ${codesSource === "fallback" ? "platform" : "carrier"} default`;

  return (
    <div style={{ maxWidth: 1040 }}>
      <p style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Procedure code defaults</p>
      <p style={{ color: "#555", marginBottom: 20 }}>
        When we pull your patients' insurance breakdowns we use a default set of procedure codes
        and coverage categories (Preventative / Basic / Major / Orthodontics). Review them per
        insurance carrier and override any for your office — or keep the defaults and set this up
        later from My Office → Insurance Carriers.
      </p>

      <div style={{ marginBottom: 18, maxWidth: 460 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Insurance carrier</div>
        <Select
          style={{ width: "100%" }}
          placeholder="Pick a carrier to review or override"
          value={providerId || undefined}
          onChange={loadCarrier}
          showSearch
          optionFilterProp="label"
          options={providers.map((p) => ({ value: p.id, label: p.name || p.code }))}
        />
      </div>

      {providerId ? (
        loadingCarrier ? (
          <div style={{ padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <div style={{ border: "1px solid #e0ddf7", borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 600 }}>Breakdown procedure codes</span>
                <Tag color={hasCodeOverride ? "purple" : "default"}>{codeBadge}</Tag>
              </div>
              <Input.TextArea
                value={codesText}
                onChange={(e) => setCodesText(e.target.value)}
                autoSize={{ minRows: 4, maxRows: 12 }}
                placeholder="One CDT code per line. Clear the box and save to revert to the default."
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>Code → coverage category</span>
                {hasCatOverride && <Tag color="purple">Customized</Tag>}
              </div>
              <div style={{ color: "#8a8699", fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
                The default category we assign each code we fetch (Preventative / Basic / Major /
                Orthodontics). Change any to override it for your office; codes left as-is keep the default.
              </div>
              <CategoryMapEditor rows={catRows} onChange={setCatRows} validCategories={validCategories} />
            </div>
            <Button onClick={saveCarrier} loading={saving} type="primary">
              Save for this carrier
            </Button>
          </div>
        )
      ) : null}

      <Button
        onClick={advance}
        loading={advancing}
        className="login-button"
        style={{ marginTop: 8, marginBottom: 12 }}
      >
        Continue
      </Button>
      <div className="skip-btn mt5" style={{ cursor: "pointer" }} onClick={advance}>
        Set up later
      </div>
    </div>
  );
};

export default ProcedureCodeDefaults;
